import { useEffect, useState } from "react";
import Link from "@/components/ui/Link";
import { getBlogCategoryUrl, getBlogAuthorUrl } from "@/lib/blog";
import { sanitizeHTML } from "@/lib/utils";
import Image from 'next/image';
import { getReadableImageUrl } from '@/lib/directus';
import { htmlToPlainText, cleanBlogContent } from '../utils';
import Breadcrumbs from "@/components/Secondary/Breadcrumbs/Breadcrumbs";
import AuthorDisplay from "../AuthorDisplay";

export interface BlogPostSectionData {
  title: string;
  published_at: string;
  content: string;
  featured_image?: {
    id: string;
    filename_download: string;
		title: string;
  };
  author?: {
    slug: string;
    title: string;
  };
  categories: {
    blog_categories_id: {
      title: string;
      slug: string;
    };
  }[];
  previousPost?: {
    slug: string;
  };
  nextPost?: {
    slug: string;
  };
}

interface BlogPostSectionProps {
  data: BlogPostSectionData;
}

export default function BlogPostSection({ data }: BlogPostSectionProps) {
  // console.log('BlogPostSection data:', data);
  // console.log('Featured image:', data.featured_image);

  const [shareUrl, setShareUrl] = useState('');
  const shareTitle = htmlToPlainText(data.title) || '';
  const imageAlt = htmlToPlainText(data.featured_image?.title || '') || (htmlToPlainText(data.title) || '');

  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  return (
		<>
			<section className='BlogHeroPostSection w-full bg-[#919985] py-12 md:py-16'>
				<div className='container text-white grid gap-2'>
					{data.categories && data.categories.length > 0 && (
						<div className='flex gap-2 flex-wrap mb-2'>
							{data.categories.map((category, index) => (
								<span key={category.blog_categories_id.slug}>
									<Link
										href={getBlogCategoryUrl(category.blog_categories_id.slug)}
										className="block text-xs md:text-md bg-white py-2 px-4 text-black transition-colors uppercase"
									>
										{category.blog_categories_id.title}
									</Link>
								</span>
							))}
						</div>
					)}
					<h1 dangerouslySetInnerHTML={sanitizeHTML(data.title)} />
					<div className='grid'>
						<div>
							<AuthorDisplay author={data.author} />
						</div>
						{/* <div className='flex gap-2'>
							<span>
								{new Date(data.published_at)
									.toLocaleDateString('en-US', {
										day: 'numeric',
										month: 'short',
										year: 'numeric',
									})
									.split(' ')
									.join(' ')}
							</span>
							<span>•</span>
							<span>5 min read</span>
						</div> */}
					</div>
				</div>
			</section>
			<Breadcrumbs />
			<section className='BlogPostSection pb-16 py-8 md:py-24'>
				<div className='container max-w-[1024px] mx-auto md:px-0'>
					{/* Navigation Buttons */}
					<div className='flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
						<Link
							href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}`}
							className='w-full md:w-auto px-8 py-4 bg-brown text-neutral-50 rounded text-center hover:bg-brown-light transition-colors'
						>
							Back to Main Page
						</Link>
						<div className='grid grid-cols-2 gap-4'>
							{data.previousPost ? (
								<Link
									href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.previousPost.slug}`}
									className='w-full px-8 py-4 bg-brown text-neutral-50 rounded text-center hover:bg-brown-light transition-colors'
								>
									Previous
								</Link>
							) : (
								<button
									disabled
									className='w-full px-8 py-4 bg-gray-400 text-neutral-50 rounded cursor-not-allowed text-center'
								>
									Previous
								</button>
							)}
							{data.nextPost ? (
								<Link
									href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.nextPost.slug}`}
									className='w-full px-8 py-4 bg-brown text-neutral-50 rounded text-center hover:bg-brown-light transition-colors'
								>
									Next
								</Link>
							) : (
								<button
									disabled
									className='w-full px-8 py-4 bg-gray-400 text-neutral-50 rounded cursor-not-allowed text-center'
								>
									Next
								</button>
							)}
						</div>
					</div>
					{data.featured_image && (
						<div className='relative aspect-[16/9] md:aspect-[16/9] aspect-[8/9] w-screen md:w-full -mx-6 md:mx-0 my-8'>
            <Image
								src={
									data.featured_image
										? getReadableImageUrl(
												data.featured_image.id,
												data.featured_image.filename_download
										  )
										: '/images/blog-placeh2.webp'
								}
              alt={imageAlt}
								fill
								className='object-cover'
								sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
								quality={95}
								loading='lazy'
							/>
						</div>
					)}
					<div
						className='grid gap-8 text_box [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:mb-6 [&_img]:block [&_img]:mx-auto'
						dangerouslySetInnerHTML={sanitizeHTML(
							cleanBlogContent(data.content)
						)}
					></div>

					{/* Social Share Buttons */}
					<div className='mt-12 flex items-center justify-end gap-6'>
						<span className='text-lg'>Share:</span>
						<div className='flex items-center gap-4'>
							<a
								href={shareLinks.facebook}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-black text-black hover:bg-gray-50 transition-colors'
								aria-label='Share on Facebook'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z' />
								</svg>
							</a>
							<a
								href={shareLinks.twitter}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-black text-black hover:bg-gray-50 transition-colors'
								aria-label='Share on Twitter'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
								</svg>
							</a>
							<a
								href={shareLinks.linkedin}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-black text-black hover:bg-gray-50 transition-colors'
								aria-label='Share on LinkedIn'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
								</svg>
							</a>
						</div>
					</div>
				</div>
			</section>
		</>
	)
}
